// =============================================================================
// DashboardPanels.js  (v1.3)
// v1.3: FIX — resizes now reliably persist into the loaded dashboard. The
//       ResizeObserver's initial measurement of an unsized card is folded
//       into the loaded snapshot (baseline), so only real user resizes /
//       moves mark the dashboard dirty and the amber "Save changes" button
//       appears exactly when the layout differs from what is stored. Save
//       calls also send panelIds alongside items for backend compatibility.
// v1.2: resize capture moved to a ResizeObserver per card (the previous
//       mouse-up hook missed native resize drags, so sizes never reached
//       the dashboard). When a LOADED dashboard's panels are moved,
//       resized, added or removed, an amber "Save changes to <name>"
//       button appears and writes the arrangement straight into that
//       dashboard (no name prompt); Save-as remains for new copies.
// v1.1: panels are RESIZABLE (CSS resize handle bottom-right; sizes stored
//       per canvas item and saved with the dashboard — backend v39.1
//       items [{id,w,h}]); per-panel POLL INTERVAL selector in the header
//       (10s-15m, persists on the panel profile and restarts the loop);
//       drag-to-reorder moved to the header row so it doesn't fight the
//       resize handle.
// -----------------------------------------------------------------------------
// AI-assisted head-office dashboard panels (headoffice.js v39 backend).
//  - "Panels" dropdown top-left adds a saved panel to the canvas; Manage
//    popup creates / edits / deletes panels. A panel = name + free-text
//    DESCRIPTION + poll rate; the description is interpreted server-side
//    into a CONSTRAINED spec (never a raw query) with a plain-English
//    readback shown at the top of the panel so misinterpretations are
//    visible immediately.
//  - Each canvas panel header shows: name, what it shows (readback), the
//    refresh interval and last-refresh time. Bodies render bar / counter /
//    table / line / live feed. Every panel polls at its own rate.
//  - Panels can be dragged to reorder / swap. The arrangement saves as a
//    named DASHBOARD, listed in the top menu bar (click to load, ✕ delete).
// =============================================================================
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import API_BASE from "./apiBase";

const API = `${API_BASE}`;
const token = () => localStorage.getItem("token") || localStorage.getItem("authToken") || "";
async function post(path, body) {
  const res = await fetch(`${API}${path}`, {
    method: "POST",
    headers: { "Content-Type": "application/json", Authorization: `Bearer ${token()}` },
    body: JSON.stringify(body || {})
  });
  const d = await res.json().catch(() => ({}));
  // The delivery API answers { error: { code, message } }; the pharmacy one
  // answered a bare string. Read both, never print an object.
  if (!res.ok) throw new Error(d?.error?.message || (typeof d.error === "string" ? d.error : null) || `HTTP ${res.status}`);
  return d;
}
const S = {
  btn: (c, solid) => ({ background: solid ? c : "#fff", color: solid ? "#fff" : c, border: `1px solid ${c}`,
    borderRadius: 8, padding: "5px 11px", fontSize: 12.5, fontWeight: 700, cursor: "pointer" }),
  inp: { border: "1px solid #d8dee8", borderRadius: 8, padding: "6px 9px", fontSize: 13 },
  lab: { fontSize: 11, fontWeight: 800, letterSpacing: 0.4, color: "#6b7280", textTransform: "uppercase" }
};
const fmtT = (iso) => iso ? new Date(iso).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit", second: "2-digit" }) : "—";
const fmtDT = (iso) => iso ? new Date(iso).toLocaleString("en-GB", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : "—";

/* ---------------- visual renderers (no chart library) ---------------- */
function BarViz({ rows }) {
  const max = Math.max(1, ...rows.map(r => r.value));
  return (
    <div>
      {rows.slice(0, 14).map(r => (
        <div key={r.label} style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
          <div style={{ width: 130, fontSize: 11.5, color: "#374151", textAlign: "right", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={r.label}>{r.label}</div>
          <div style={{ flex: 1, background: "#f1f5f9", borderRadius: 4, height: 18, position: "relative" }}>
            <div style={{ width: `${(r.value / max) * 100}%`, background: "#0f766e", height: "100%", borderRadius: 4 }} />
            <span style={{ position: "absolute", right: 6, top: 1, fontSize: 11, fontWeight: 800, color: "#111" }}>{r.value}</span>
          </div>
        </div>
      ))}
      {rows.length === 0 && <div style={{ color: "#9ca3af", fontSize: 12.5 }}>No data in this window.</div>}
    </div>
  );
}
function LineViz({ rows }) {
  if (!rows.length) return <div style={{ color: "#9ca3af", fontSize: 12.5 }}>No data in this window.</div>;
  const W = 420, H = 120, max = Math.max(1, ...rows.map(r => r.value));
  const pts = rows.map((r, i) => `${(i / Math.max(1, rows.length - 1)) * (W - 20) + 10},${H - 12 - (r.value / max) * (H - 30)}`).join(" ");
  return (
    <svg viewBox={`0 0 ${W} ${H}`} style={{ width: "100%", height: "auto" }}>
      <polyline points={pts} fill="none" stroke="#0f766e" strokeWidth="2.5" />
      {rows.map((r, i) => (
        <circle key={i} cx={(i / Math.max(1, rows.length - 1)) * (W - 20) + 10} cy={H - 12 - (r.value / max) * (H - 30)} r="3" fill="#0f766e">
          <title>{r.label}: {r.value}</title>
        </circle>
      ))}
      <text x={10} y={H - 2} fontSize="9" fill="#6b7280">{rows[0].label}</text>
      <text x={W - 10} y={H - 2} fontSize="9" fill="#6b7280" textAnchor="end">{rows[rows.length - 1].label}</text>
    </svg>
  );
}
function TableViz({ rows }) {
  return (
    <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12.5 }}>
      <tbody>
        {rows.slice(0, 20).map(r => (
          <tr key={r.label} style={{ borderBottom: "1px solid #f1f5f9" }}>
            <td style={{ padding: "4px 6px" }}>{r.label}</td>
            <td style={{ padding: "4px 6px", textAlign: "right", fontWeight: 800 }}>{r.value}</td>
          </tr>
        ))}
        {rows.length === 0 && <tr><td style={{ color: "#9ca3af", padding: 6 }}>No data in this window.</td></tr>}
      </tbody>
    </table>
  );
}
function FeedViz({ items }) {
  return (
    <div style={{ maxHeight: 260, overflowY: "auto" }}>
      {items.map((it, i) => (
        <div key={i} style={{ borderBottom: "1px solid #f1f5f9", padding: "5px 2px", fontSize: 12.5 }}>
          <span style={{ fontWeight: 800, color: "#0f766e" }}>{it.branch || it.pharmacy}</span>
          <span style={{ color: "#6b7280", marginLeft: 6, fontSize: 11.5 }}>{fmtDT(it.when)}</span>
          <div>{it.text}</div>
        </div>
      ))}
      {items.length === 0 && <div style={{ color: "#9ca3af", fontSize: 12.5 }}>Nothing yet in this window.</div>}
    </div>
  );
}
function PanelBody({ result }) {
  if (!result) return <div style={{ color: "#9ca3af", fontSize: 12.5 }}>Loading…</div>;
  if (result.error) return <div style={{ color: "#dc2626", fontSize: 12.5 }}>{result.error}</div>;
  if (result.kind === "feed") return <FeedViz items={result.items || []} />;
  const rows = result.rows || [];
  const visual = result.visual;
  if (visual === "counter") {
    return <div style={{ fontSize: 44, fontWeight: 900, color: "#0f766e", textAlign: "center", padding: "14px 0" }}>{result.total ?? 0}</div>;
  }
  if (visual === "line") return <LineViz rows={rows} />;
  if (visual === "table") return <TableViz rows={rows} />;
  return <BarViz rows={rows} />;
}

/* ---------------- panel editor dialog ---------------- */
function PanelEditor({ initial, onClose, onSaved }) {
  const [name, setName] = useState(initial?.name || "");
  const [description, setDescription] = useState(initial?.description || "");
  const [pollSec, setPollSec] = useState(initial?.pollSec || 60);
  const [spec, setSpec] = useState(initial?.spec || null);
  const [readback, setReadback] = useState(initial?.readback || "");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const interpret = async () => {
    setBusy(true); setErr(null);
    try {
      const d = await post("/api/headoffice/dash/spec", { description });
      setSpec(d.spec); setReadback(d.readback);
    } catch (e) { setErr(e.message); } finally { setBusy(false); }
  };
  const save = async () => {
    setBusy(true); setErr(null);
    try {
      let s = spec, rb = readback;
      if (!s) { const d = await post("/api/headoffice/dash/spec", { description }); s = d.spec; rb = d.readback; }
      const r = await post("/api/headoffice/dash/panels/save",
        { id: initial?._id, name, description, pollSec, spec: s, readback: rb });
      onSaved(r.id);
    } catch (e) { setErr(e.message); setBusy(false); }
  };
  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 9600 }}>
      <div style={{ background: "#fff", borderRadius: 8, width: "min(620px, 94vw)", maxHeight: "92vh", overflowY: "auto", padding: 18 }}>
        <div style={{ fontWeight: 800, fontSize: 15, marginBottom: 10 }}>{initial?._id ? "Edit panel" : "New panel"}</div>
        {err && <div style={{ color: "#dc2626", fontSize: 12.5, marginBottom: 8 }}>{err}</div>}
        <div style={S.lab}>Panel name</div>
        <input style={{ ...S.inp, width: "100%", boxSizing: "border-box", marginBottom: 10 }} value={name} onChange={e => setName(e.target.value)} placeholder="e.g. Outstanding by driver" />
        <div style={S.lab}>Describe what this panel should show</div>
        <textarea rows={3} value={description} onChange={e => { setDescription(e.target.value); setSpec(null); setReadback(""); }}
          placeholder={`e.g. "Outstanding deliveries by driver"\nor "Collections in the van by branch"\nor "Deliveries delivered per day over the last 7 days"\nor "A live feed of failed delivery attempts"`}
          style={{ ...S.inp, width: "100%", boxSizing: "border-box", fontFamily: "inherit", resize: "vertical", marginBottom: 10 }} />
        <div style={{ display: "flex", gap: 12, alignItems: "flex-end", marginBottom: 10 }}>
          <div><div style={S.lab}>Refresh every (seconds)</div>
            <input type="number" min="10" max="3600" value={pollSec} onChange={e => setPollSec(e.target.value)} style={{ ...S.inp, width: 100 }} /></div>
          <button style={S.btn("#3730a3")} disabled={busy || !description.trim()} onClick={interpret}>
            {busy ? "Working…" : "Interpret description"}
          </button>
        </div>
        {readback && (
          <div style={{ background: "#f0fdfa", border: "1px solid #99f6e4", borderRadius: 6, padding: "8px 10px", fontSize: 13, marginBottom: 10 }}>
            <b>Understood as:</b> {readback}
          </div>
        )}
        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
          <button style={S.btn("#6b7280")} disabled={busy} onClick={onClose}>Cancel</button>
          <button style={{ ...S.btn("#0f766e", true) }} disabled={busy || !name.trim() || !description.trim()} onClick={save}>Save panel</button>
        </div>
      </div>
    </div>
  );
}

/* ---------------- manage popup ---------------- */
function ManagePanels({ panels, onClose, onEdit, onDelete, onNew }) {
  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 9500 }} onClick={onClose}>
      <div style={{ background: "#fff", borderRadius: 8, width: "min(640px, 94vw)", maxHeight: "84vh", overflowY: "auto", padding: 16 }} onClick={e => e.stopPropagation()}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
          <div style={{ fontWeight: 800 }}>Manage panels</div>
          <div style={{ display: "flex", gap: 8 }}>
            <button style={S.btn("#0f766e", true)} onClick={onNew}>+ New panel</button>
            <button style={S.btn("#6b7280")} onClick={onClose}>Close</button>
          </div>
        </div>
        {panels.length === 0 && <div style={{ color: "#6b7280", fontSize: 13 }}>No panels yet — create one.</div>}
        {panels.map(p => (
          <div key={p._id} style={{ border: "1px solid #e5e7eb", borderRadius: 6, padding: "8px 10px", marginBottom: 8 }}>
            <div style={{ display: "flex", justifyContent: "space-between", gap: 8 }}>
              <div>
                <b>{p.name}</b> <span style={{ color: "#6b7280", fontSize: 11.5 }}>every {p.pollSec}s</span>
                <div style={{ fontSize: 12.5, color: "#374151" }}>{p.description}</div>
                {p.readback && <div style={{ fontSize: 11.5, color: "#0f766e", fontStyle: "italic" }}>{p.readback}</div>}
              </div>
              <div style={{ display: "flex", gap: 6, alignItems: "flex-start" }}>
                <button style={S.btn("#4338ca")} onClick={() => onEdit(p)}>Edit</button>
                <button style={S.btn("#dc2626")} onClick={() => onDelete(p)}>Delete</button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ================================ main ================================ */
/**
 * @param {string} [wallDashboardId] render this dashboard alone, with no
 *   toolbar, no dashboards bar and no editing - for a screen on the wall.
 */
export default function DashboardPanels({ wallDashboardId = null }) {
  const wall = !!wallDashboardId;
  const [panels, setPanels] = useState([]);
  const [dashboards, setDashboards] = useState([]);
  const [canvas, setCanvas] = useState([]);           // ordered items [{id, w, h}] — w/h from the resize handles
  const [results, setResults] = useState({});          // panelId -> payload
  const [manageOpen, setManageOpen] = useState(false);
  const [editor, setEditor] = useState(null);          // null | {} | panel
  const [dropdown, setDropdown] = useState("");
  const [err, setErr] = useState(null);
  const [activeDash, setActiveDash] = useState(null);
  const timers = useRef({});
  const dragIdx = useRef(null);
  const cardRefs = useRef({});
  const loadedSnap = useRef("[]"); // canvas snapshot when the active dashboard was loaded/saved

  const byId = useMemo(() => Object.fromEntries(panels.map(p => [String(p._id), p])), [panels]);

  const loadMeta = useCallback(async () => {
    try {
      const [p, d] = await Promise.all([post("/api/headoffice/dash/panels"), post("/api/headoffice/dash/dashboards")]);
      setPanels(p.panels || []); setDashboards(d.dashboards || []);
    } catch (e) { setErr(e.message); }
  }, []);
  useEffect(() => { loadMeta(); }, [loadMeta]);

  // Wall mode: open the requested dashboard as soon as the panel list is in.
  // It waits for `panels` because loadDashboard filters items against byId -
  // running before they arrive would silently produce an empty canvas.
  useEffect(() => {
    if (!wall || !dashboards.length || !panels.length || activeDash) return;
    const d = dashboards.find(x => String(x._id) === String(wallDashboardId));
    if (d) loadDashboard(d);
    else setErr("That dashboard no longer exists.");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wall, wallDashboardId, dashboards, panels, activeDash]);

  const runPanel = useCallback(async (panel) => {
    try {
      const d = await post("/api/headoffice/dash/run", { spec: panel.spec });
      setResults(r => ({ ...r, [String(panel._id)]: { ...d, visual: panel.spec.visual } }));
    } catch (e) {
      setResults(r => ({ ...r, [String(panel._id)]: { error: e.message } }));
    }
  }, []);

  // (re)build poll timers whenever the canvas or panel definitions change
  useEffect(() => {
    Object.values(timers.current).forEach(clearInterval);
    timers.current = {};
    for (const it of canvas) {
      const p = byId[it.id];
      if (!p) continue;
      runPanel(p);
      timers.current[it.id] = setInterval(() => runPanel(p), (p.pollSec || 60) * 1000);
    }
    return () => { Object.values(timers.current).forEach(clearInterval); timers.current = {}; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canvas, panels]);

  const addToCanvas = (id) => { if (id && !canvas.some(it => it.id === id)) setCanvas(c => [...c, { id, w: null, h: null }]); setDropdown(""); };
  const removeFromCanvas = (id) => setCanvas(c => c.filter(x => x.id !== id));
  const setSize = (id, w, h) => {
    // Ignore degenerate measurements (element hidden / not yet laid out) —
    // they must never overwrite a stored size.
    if (!(w >= 60) || !(h >= 60)) return;
    setCanvas(c => {
      const cur = c.find(it => it.id === id);
      if (!cur) return c;
      const isBaseline = cur.w == null && cur.h == null;
      if (!isBaseline && Math.abs((cur.w || 0) - w) <= 2 && Math.abs((cur.h || 0) - h) <= 2) return c;
      const next = c.map(it => it.id === id ? { ...it, w, h } : it);
      if (isBaseline && loadedSnap.current) {
        // First measurement of a card the dashboard stored without a size —
        // fold it into the snapshot too so it doesn't read as a user change.
        try {
          const snap = JSON.parse(loadedSnap.current);
          loadedSnap.current = JSON.stringify(snap.map(it => it.id === id ? { ...it, w, h } : it));
        } catch (_) {}
      }
      return next;
    });
  };

  // Reliable resize capture: a ResizeObserver on every card (the mouse-up
  // from a native resize drag doesn't always land on the card itself).
  useEffect(() => {
    if (typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(entries => {
      for (const en of entries) {
        const id = en.target.getAttribute("data-pid");
        if (id) setSize(id, Math.round(en.target.offsetWidth), Math.round(en.target.offsetHeight));
      }
    });
    for (const it of canvas) { const el = cardRefs.current[it.id]; if (el) ro.observe(el); }
    return () => ro.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canvas.map(it => it.id).join(",")]);

  // A loaded dashboard is DIRTY when panels have been moved, resized, added
  // or removed since it was loaded/saved — exposes "Save changes".
  const dirty = !!activeDash?._id && JSON.stringify(canvas) !== loadedSnap.current;

  const onDrop = (idx) => {
    const from = dragIdx.current;
    if (from === null || from === idx) return;
    setCanvas(c => { const n = [...c]; const [moved] = n.splice(from, 1); n.splice(idx, 0, moved); return n; });
    dragIdx.current = null;
  };

  // per-panel poll interval quick-change: persists on the panel profile and
  // the timers effect (depends on panels) restarts the loop at the new rate.
  const changeInterval = async (p, sec) => {
    try {
      await post("/api/headoffice/dash/panels/save",
        { id: p._id, name: p.name, description: p.description, pollSec: sec, spec: p.spec, readback: p.readback });
      await loadMeta();
    } catch (e) { setErr(e.message); }
  };

  const saveDashboard = async () => {
    if (!canvas.length) { window.alert("Add at least one panel first."); return; }
    const name = window.prompt("Dashboard name:", activeDash?.name || "");
    if (!name) return;
    try {
      const existing = dashboards.find(d => d.name === name);
      const r = await post("/api/headoffice/dash/dashboards/save",
        { id: existing?._id, name, items: canvas, panelIds: canvas.map(it => it.id) });
      loadedSnap.current = JSON.stringify(canvas);
      const d2 = await post("/api/headoffice/dash/dashboards");
      setDashboards(d2.dashboards || []);
      setActiveDash((d2.dashboards || []).find(x => x.name === name) || { _id: existing?._id || r.id, name });
    } catch (e) { setErr(e.message); }
  };
  const loadDashboard = (d) => {
    setActiveDash(d);
    const items = (Array.isArray(d.items) && d.items.length ? d.items : (d.panelIds || []).map(id => ({ id, w: null, h: null })))
      .filter(it => byId[it.id]).map(it => ({ id: String(it.id), w: it.w ?? null, h: it.h ?? null }));
    setCanvas(items);
    loadedSnap.current = JSON.stringify(items);
  };

  // one-click save of moves/resizes into the CURRENTLY OPEN dashboard
  const saveChanges = async () => {
    if (!activeDash?._id) return;
    try {
      await post("/api/headoffice/dash/dashboards/save",
        { id: activeDash._id, name: activeDash.name, items: canvas, panelIds: canvas.map(it => it.id) });
      loadedSnap.current = JSON.stringify(canvas);
      await loadMeta();
    } catch (e) { setErr(e.message); }
  };
  const deleteDashboard = async (d) => {
    if (!window.confirm(`Delete dashboard "${d.name}"? (Its panels are kept.)`)) return;
    try { await post("/api/headoffice/dash/dashboards/delete", { id: d._id }); await loadMeta(); if (activeDash?.name === d.name) setActiveDash(null); }
    catch (e) { setErr(e.message); }
  };
  const deletePanel = async (p) => {
    if (!window.confirm(`Delete panel "${p.name}"?`)) return;
    try { await post("/api/headoffice/dash/panels/delete", { id: p._id }); removeFromCanvas(String(p._id)); await loadMeta(); }
    catch (e) { setErr(e.message); }
  };

  return (
    <div style={{ padding: wall ? "10px 12px" : "14px 18px", minHeight: "100vh",
                  background: "#f8fafc" }}>
      {/* Wall mode: a small, unobtrusive header. The dashboard name is worth
          keeping - a screen showing figures with no label is a screen nobody
          trusts - but nothing here is interactive. */}
      {wall && (
        <div style={{ display: "flex", alignItems: "baseline", gap: 12, marginBottom: 10 }}>
          <h2 style={{ margin: 0, fontSize: 18, color: "#0f766e" }}>
            {activeDash?.name || "Dashboard"}
          </h2>
          <span style={{ fontSize: 12, color: "#9ca3af" }}>
            live · panels refresh on their own schedule
          </span>
          {err && <span style={{ color: "#dc2626", fontSize: 12.5 }}>{err}</span>}
        </div>
      )}

      {/* top bar */}
      {!wall && (
      <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", marginBottom: 6 }}>
        <h2 style={{ margin: 0, fontSize: 20, color: "#0f766e" }}>Dashboard panels</h2>
        <select value={dropdown} onChange={e => addToCanvas(e.target.value)} style={{ ...S.inp, minWidth: 220 }}>
          <option value="">Panels — add to canvas…</option>
          {panels.map(p => <option key={p._id} value={String(p._id)} disabled={canvas.some(it => it.id === String(p._id))}>{p.name}</option>)}
        </select>
        <button style={S.btn("#0f766e")} onClick={() => setManageOpen(true)}>Manage panels</button>
        <button style={S.btn("#3730a3")} onClick={saveDashboard}>Save as dashboard</button>
        {dirty && (
          <button style={{ ...S.btn("#b45309", true), background: "#f59e0b", border: "1px solid #b45309" }} onClick={saveChanges}
            title="Panels have been moved, resized, added or removed — save the new arrangement into this dashboard.">
            💾 Save changes to "{activeDash.name}"
          </button>
        )}
        {activeDash?._id && (
          <button style={S.btn("#0f766e")}
            title="Open this dashboard on its own in a new tab - no menu, no toolbar - so it can be dragged to another monitor and left running."
            onClick={() => window.open(
              `${window.location.pathname}?wall=${activeDash._id}`, "_blank", "noopener")}>
            🖥 Open on another screen
          </button>
        )}
        {activeDash && !dirty && <span style={{ fontSize: 12.5, color: "#6b7280" }}>Viewing: <b>{activeDash.name}</b> (saved)</span>}
        {err && <span style={{ color: "#dc2626", fontSize: 12.5 }}>{err}</span>}
      </div>
      )}
      {/* dashboards menu bar */}
      {!wall && (
      <div style={{ display: "flex", gap: 6, flexWrap: "wrap", borderBottom: "1px solid #e5e7eb", paddingBottom: 8, marginBottom: 12 }}>
        <span style={{ ...S.lab, alignSelf: "center" }}>Dashboards:</span>
        {dashboards.length === 0 && <span style={{ fontSize: 12.5, color: "#9ca3af" }}>none saved yet</span>}
        {dashboards.map(d => (
          <span key={d._id} style={{ display: "inline-flex", alignItems: "center", gap: 4, border: "1px solid #99f6e4",
            background: activeDash?.name === d.name ? "#0f766e" : "#f0fdfa", color: activeDash?.name === d.name ? "#fff" : "#0f766e",
            borderRadius: 6, padding: "3px 6px 3px 10px", fontSize: 12.5, fontWeight: 700 }}>
            <span style={{ cursor: "pointer" }} onClick={() => loadDashboard(d)}>{d.name}</span>
            <span style={{ cursor: "pointer", padding: "0 4px" }} title="Delete dashboard" onClick={() => deleteDashboard(d)}>✕</span>
          </span>
        ))}
      </div>
      )}

      {/* canvas */}
      {canvas.length === 0 && (
        <div style={{ color: "#6b7280", fontSize: 14, padding: 30, textAlign: "center", border: "2px dashed #e5e7eb", borderRadius: 10 }}>
          {wall
            ? "Loading dashboard…"
            : <>Pick a panel from the dropdown, or open <b>Manage panels</b> to create your first one.</>}
        </div>
      )}
      <div style={{ display: "flex", flexWrap: "wrap", gap: 12, alignItems: "flex-start" }}>
        {canvas.map((it, idx) => {
          const id = it.id;
          const p = byId[id];
          if (!p) return null;
          const res = results[id];
          return (
            <div key={id} data-pid={id} ref={el => { cardRefs.current[id] = el; }}
              onDragOver={e => e.preventDefault()}
              onDrop={() => onDrop(idx)}
              style={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: 10, padding: "10px 12px",
                       width: it.w || 430, height: it.h || undefined,
                       minWidth: 300, minHeight: 170, maxWidth: "100%",
                       resize: wall ? "none" : "both", overflow: "auto", boxSizing: "border-box" }}>
              <div draggable={!wall}
                onDragStart={() => { if (!wall) dragIdx.current = idx; }}
                style={{ display: "flex", justifyContent: "space-between", gap: 6, cursor: "grab" }}
                title="Drag to reorder. Use the bottom-right corner to resize.">
                <div style={{ fontWeight: 800, fontSize: 14 }}>{p.name}</div>
                <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                  <select value={p.pollSec || 60} title="Poll interval"
                    onChange={e => changeInterval(p, Number(e.target.value))}
                    style={{ ...S.inp, padding: "1px 4px", fontSize: 11 }}>
                    {[10, 30, 60, 120, 300, 900].map(s => <option key={s} value={s}>{s < 60 ? `${s}s` : `${s / 60}m`}</option>)}
                  </select>
                  <button style={{ ...S.btn("#0f766e"), padding: "1px 7px" }} title="Refresh now" onClick={() => runPanel(p)}>↻</button>
                  {!wall && (
                    <button style={{ ...S.btn("#6b7280"), padding: "1px 7px" }} title="Remove from canvas" onClick={() => removeFromCanvas(id)}>✕</button>
                  )}
                </div>
              </div>
              {/* what it shows + refresh interval — always at the top of the panel */}
              <div style={{ fontSize: 11.5, color: "#0f766e", fontStyle: "italic", margin: "2px 0 1px" }}>{p.readback || p.description}</div>
              <div style={{ fontSize: 10.5, color: "#9ca3af", marginBottom: 8 }}>
                Refreshes every {p.pollSec}s · last {fmtT(res?.generatedAt)}
              </div>
              <PanelBody result={res} />
            </div>
          );
        })}
      </div>

      {manageOpen && (
        <ManagePanels
          panels={panels}
          onClose={() => setManageOpen(false)}
          onNew={() => setEditor({})}
          onEdit={(p) => setEditor(p)}
          onDelete={deletePanel}
        />
      )}
      {editor && (
        <PanelEditor
          initial={editor._id ? editor : null}
          onClose={() => setEditor(null)}
          onSaved={async () => { setEditor(null); await loadMeta(); }}
        />
      )}
    </div>
  );
}